import Navbar, { NAV, DEPARTMENT_IDS } from '../components/Navbar/Navbar';
import { useCallback, useEffect, useState } from 'react';
import ARRMain from '../pages/ARR_Main';
import ARMain from '../pages/ARMain';
import PipelineMain from '../pages/PipelineMain';
import LegalMain from '../pages/LegalMain';
import GAMain from '../pages/GAMain';
import MarketingMain from '../pages/MarketingMain';
import RnDMain from '../pages/RnDMain';
import CSMain from '../pages/CSMain';
import DevOpsMain from '../pages/DevOpsMain';
import PMMain from '../pages/PMMain';
import APACMain from '../pages/APACMain';
import NAMMain from '../pages/NAMMain';
import SOLMain from '../pages/SOLMain';
import DepartmentMain from '../pages/DepartmentMain';
import { DevOverlay } from '../components/DevOverlay/DevOverlay';
import Settings from '../components/Settings/Settings';
import AccessControl from '../components/Settings/AccessControl';
import GitHub from '../components/Settings/GitHub';
import Profile from '../components/Profile/Profile';

function MainLayout({ user, onLogout }) {
    const [path, setPath] = useState(window.location.pathname);
    const [lastRefreshed, setLastRefreshed] = useState(null);
    // Stable identity is required here — each dashboard puts this in a
    // useCallback/useEffect dependency array for its data fetch, so a new
    // function reference on every MainLayout render would trigger an
    // infinite refetch loop.
    const markRefreshed = useCallback(() => setLastRefreshed(Date.now()), []);
    const access = user?.isSuperuser ? ['administrator', 'developer', 'pipeline', 'ar', 'arr', 'legal', 'ga', 'marketing', 'rnd', 'cs', 'devops', 'pm', 'apac', 'nam', 'sol'] : (user?.access || []);
    const canOpen = (key) => user?.isSuperuser || access.includes(key);

    useEffect(() => {
        // Clear the "last refreshed" indicator on every navigation — it
        // belongs to whichever dashboard is now loading, not the one the
        // user just left, and will be set again once the new one finishes.
        const syncPath = () => { setPath(window.location.pathname); setLastRefreshed(null); };
        window.addEventListener('popstate', syncPath);
        return () => window.removeEventListener('popstate', syncPath);
    }, []);

    let currentPath = path;
    if (currentPath === '/') {
        // Land on whichever dashboard the user actually has access to —
        // same NAV order the nav menu itself uses for visibleNav[0], rather
        // than a hardcoded pipeline-first chain that skipped legal/G&A
        // entirely and sent anyone without pipeline/arr/ar access to
        // Settings even when they did have Legal or G&A access. The
        // department dashboards redirect to the grouped /department
        // page (with its own sub-tab bar) instead of straight to their own
        // standalone route, matching how the nav bar itself only links to
        // /department now.
        const firstItem = NAV.find(item => canOpen(item.id));
        const firstPath = firstItem ? (DEPARTMENT_IDS.includes(firstItem.id) ? '/department' : firstItem.href) : '/settings';
        window.history.replaceState(null, '', firstPath);
        currentPath = firstPath;
    }

    const isPipeline = currentPath.startsWith('/pipeline');
    const isSettings = currentPath === '/settings' || currentPath.startsWith('/settings/');
    const isAccessControl = currentPath === '/settings/access-control';
    const isGitHub = currentPath === '/settings/github';
    const isProfile = currentPath === '/profile';
    const isAR = !isPipeline && (currentPath === '/ar' || currentPath.startsWith('/ar/'));
    const isLegal = !isPipeline && !isAR && (currentPath === '/legal' || currentPath.startsWith('/legal/'));
    const isDepartment = !isPipeline && !isAR && !isLegal && (currentPath === '/department' || currentPath.startsWith('/department/'));
    // Direct /ga, /marketing, /rnd, /cs routes stay reachable
    // standalone (existing bookmarks/deep links keep working) even though
    // the nav bar only links to /department now — Department Dashboard's
    // own sub-tab bar is just a second way in.
    const isGA = !isPipeline && !isAR && !isLegal && !isDepartment && (currentPath === '/ga' || currentPath.startsWith('/ga/'));
    const isMarketing = !isPipeline && !isAR && !isLegal && !isDepartment && !isGA && (currentPath === '/marketing' || currentPath.startsWith('/marketing/'));
    const isRnD = !isPipeline && !isAR && !isLegal && !isDepartment && !isGA && !isMarketing && (currentPath === '/rnd' || currentPath.startsWith('/rnd/'));
    const isCS = !isPipeline && !isAR && !isLegal && !isDepartment && !isGA && !isMarketing && !isRnD && (currentPath === '/cs' || currentPath.startsWith('/cs/'));
    const isDevOps = !isPipeline && !isAR && !isLegal && !isDepartment && !isGA && !isMarketing && !isRnD && !isCS && (currentPath === '/devops' || currentPath.startsWith('/devops/'));
    const isPM = !isPipeline && !isAR && !isLegal && !isDepartment && !isGA && !isMarketing && !isRnD && !isCS && !isDevOps && (currentPath === '/pm' || currentPath.startsWith('/pm/'));
    const isAPAC = !isPipeline && !isAR && !isLegal && !isDepartment && !isGA && !isMarketing && !isRnD && !isCS && !isDevOps && !isPM && (currentPath === '/apac' || currentPath.startsWith('/apac/'));
    const isNAM = !isPipeline && !isAR && !isLegal && !isDepartment && !isGA && !isMarketing && !isRnD && !isCS && !isDevOps && !isPM && !isAPAC && (currentPath === '/nam' || currentPath.startsWith('/nam/'));
    const isSOL = !isPipeline && !isAR && !isLegal && !isDepartment && !isGA && !isMarketing && !isRnD && !isCS && !isDevOps && !isPM && !isAPAC && !isNAM && (currentPath === '/sol' || currentPath.startsWith('/sol/'));
    const isARR = !isSettings && !isPipeline && !isAR && !isLegal && !isDepartment && !isGA && !isMarketing && !isRnD && !isCS && !isDevOps && !isPM && !isAPAC && !isNAM && !isSOL && !isProfile;
    const active = isSettings || isProfile ? '' : isPipeline ? 'pipeline' : isAR ? 'ar' : isLegal ? 'legal' : isDepartment ? 'department' : isGA ? 'ga' : isMarketing ? 'marketing' : isRnD ? 'rnd' : isCS ? 'cs' : isDevOps ? 'devops' : isPM ? 'pm' : isAPAC ? 'apac' : isNAM ? 'nam' : isSOL ? 'sol' : 'arr';
    const blocked =
        (isAccessControl && !canOpen('administrator')) ||
        (isGitHub && !canOpen('developer')) ||
        (isSettings && !isAccessControl && !isGitHub && !canOpen('administrator') && !canOpen('developer')) ||
        (isPipeline && !canOpen('pipeline')) ||
        (isAR && !canOpen('ar')) ||
        (isLegal && !canOpen('legal')) ||
        (isDepartment && !DEPARTMENT_IDS.some(id => canOpen(id))) ||
        (isGA && !canOpen('ga')) ||
        (isMarketing && !canOpen('marketing')) ||
        (isRnD && !canOpen('rnd')) ||
        (isCS && !canOpen('cs')) ||
        (isDevOps && !canOpen('devops')) ||
        (isPM && !canOpen('pm')) ||
        (isAPAC && !canOpen('apac')) ||
        (isNAM && !canOpen('nam')) ||
        (isSOL && !canOpen('sol')) ||
        (isARR && !canOpen('arr'));

    return (
        <>
            <DevOverlay name="Navbar">
                <Navbar active={active} user={user} onLogout={onLogout} lastRefreshed={lastRefreshed} />
            </DevOverlay>
            <main>
                {isProfile ? <Profile user={user} /> : blocked ? <AccessDenied /> : isAccessControl ? <SettingsShell title="Access Control" backLabel="Settings" hideHeader><AccessControl user={user} /></SettingsShell> : isGitHub ? <SettingsShell title="GitHub" backLabel="Settings" hideHeader><GitHub /></SettingsShell> : isSettings ? <Settings user={user} /> : isPipeline ? <PipelineMain user={user} onDataLoaded={markRefreshed} /> : isAR ? <ARMain user={user} onDataLoaded={markRefreshed} /> : isLegal ? <LegalMain user={user} onDataLoaded={markRefreshed} /> : isDepartment ? <DepartmentMain user={user} onDataLoaded={markRefreshed} /> : isGA ? <GAMain user={user} onDataLoaded={markRefreshed} /> : isMarketing ? <MarketingMain user={user} onDataLoaded={markRefreshed} /> : isRnD ? <RnDMain user={user} onDataLoaded={markRefreshed} /> : isCS ? <CSMain user={user} onDataLoaded={markRefreshed} /> : isDevOps ? <DevOpsMain user={user} onDataLoaded={markRefreshed} /> : isPM ? <PMMain user={user} onDataLoaded={markRefreshed} /> : isAPAC ? <APACMain user={user} onDataLoaded={markRefreshed} /> : isNAM ? <NAMMain user={user} onDataLoaded={markRefreshed} /> : isSOL ? <SOLMain user={user} onDataLoaded={markRefreshed} /> : <ARRMain user={user} onDataLoaded={markRefreshed} />}
            </main>
        </>
    );
}

function SettingsShell({ title, backLabel, children, hideHeader = false }) {
    const goBack = () => {
        window.history.pushState(null, '', '/settings');
        window.dispatchEvent(new PopStateEvent('popstate'));
    };

    return <section className="settings-page">
        {!hideHeader && <header className="settings-subpage-head">
            <button type="button" onClick={goBack}>Back to {backLabel}</button>
            <div>
                <span className="settings-eyebrow">Workspace control center</span>
                <h1>{title}</h1>
            </div>
        </header>}
        <div className="settings-layout">{children}</div>
    </section>;
}

function AccessDenied() {
    return <section className="settings-page"><article className="settings-panel"><span className="settings-kicker">Access Control</span><h2>Access required</h2><p className="settings-panel-description">You do not have any role assigned in the application. Please contact the Application Administrator.</p></article></section>;
}

export default MainLayout;
